'use client';

import { useState, type FormEvent } from 'react';
import { Mail, CheckCircle, AlertCircle, Send, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';

interface NewsletterSignupProps {
  variant?: 'inline' | 'card' | 'hero';
  source?: string;
  title?: string;
  description?: string;
  placeholder?: string;
  buttonText?: string;
  className?: string;
}

export function NewsletterSignup({
  variant = 'card',
  source = 'website',
  title = 'Stay Ahead of the EV Revolution',
  description = 'Get monthly insights on EV repair, battery technology, and garage transformation. No spam, unsubscribe anytime.',
  placeholder = 'Enter your email address',
  buttonText = 'Subscribe',
  className = '',
}: NewsletterSignupProps) {
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [message, setMessage] = useState('');

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!email.trim() || status === 'loading') return;

    setStatus('loading');
    setMessage('');

    try {
      const response = await fetch('/api/newsletter/subscribe', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: email.trim(),
          name: name.trim() || undefined,
          source,
        }),
      });

      const data = await response.json();

      if (data.success) {
        setStatus('success');
        setMessage(data.message || 'Subscribed successfully!');
        setEmail('');
        setName('');
      } else {
        setStatus('error');
        setMessage(data.error || 'Subscription failed. Please try again.');
      }
    } catch {
      setStatus('error');
      setMessage('Something went wrong. Please try again later.');
    }
  };

  // Inline variant - 单行紧凑
  if (variant === 'inline') {
    return (
      <form onSubmit={handleSubmit} className={`flex gap-2 ${className}`}>
        <div className="flex-1 relative">
          <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder={placeholder}
            className="pl-9 h-11 bg-[var(--color-surface)] border-[var(--color-border)]"
            required
          />
        </div>
        <Button
          type="submit"
          disabled={status === 'loading'}
          className="h-11 px-6 bg-[oklch(0.62_0.19_150)] hover:bg-[oklch(0.48_0.17_150)] text-[var(--color-background)] font-semibold shadow-lg shadow-[var(--color-glow-soft)] transition-all"
        >
          {status === 'loading' ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            <Send className="w-4 h-4" />
          )}
          <span className="ml-2 hidden sm:inline">{buttonText}</span>
        </Button>
      </form>
    );
  }

  // Hero variant - 大标题居中
  if (variant === 'hero') {
    return (
      <div className={`text-center ${className}`}>
        <h3 className="text-2xl md:text-3xl font-bold mb-3">{title}</h3>
        <p className="text-muted-foreground mb-6 max-w-xl mx-auto">{description}</p>
        <form onSubmit={handleSubmit} className="flex flex-col sm:flex-row gap-3 max-w-md mx-auto">
          <Input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder={placeholder}
            className="h-12 bg-[var(--color-surface)] border-[var(--color-border)] text-base"
            required
          />
          <Button
            type="submit"
            disabled={status === 'loading'}
            className="h-12 px-8 bg-[oklch(0.62_0.19_150)] hover:bg-[oklch(0.48_0.17_150)] text-[var(--color-background)] font-semibold shadow-lg shadow-[var(--color-glow-soft)] whitespace-nowrap"
          >
            {status === 'loading' ? (
              <Loader2 className="w-5 h-5 animate-spin" />
            ) : (
              buttonText
            )}
          </Button>
        </form>
        {status === 'success' && (
          <div className="flex items-center justify-center gap-2 mt-4 text-[oklch(0.72_0.22_150)] text-sm">
            <CheckCircle className="w-4 h-4" />
            {message}
          </div>
        )}
        {status === 'error' && (
          <div className="flex items-center justify-center gap-2 mt-4 text-red-400 text-sm">
            <AlertCircle className="w-4 h-4" />
            {message}
          </div>
        )}
      </div>
    );
  }

  // Card variant - 默认卡片式
  return (
    <div className={`rounded-2xl border border-[var(--color-border)] bg-gradient-to-br from-[var(--color-surface)] to-transparent p-8 ${className}`}>
      <div className="flex items-start gap-4 mb-5">
        <div className="w-12 h-12 rounded-xl bg-[oklch(0.62_0.19_150)]/20 border border-[oklch(0.62_0.19_150)]/30 flex items-center justify-center flex-shrink-0">
          <Mail className="w-6 h-6 text-[oklch(0.72_0.22_150)]" />
        </div>
        <div>
          <h3 className="text-lg font-bold mb-1">{title}</h3>
          <p className="text-sm text-muted-foreground">{description}</p>
        </div>
      </div>

      {status === 'success' ? (
        <div className="flex items-center gap-3 p-4 rounded-xl bg-[oklch(0.72_0.22_150)]/10 border border-[oklch(0.72_0.22_150)]/30">
          <CheckCircle className="w-5 h-5 text-[oklch(0.72_0.22_150)] flex-shrink-0" />
          <div>
            <div className="font-medium text-[oklch(0.72_0.22_150)]">Subscribed!</div>
            <div className="text-sm text-muted-foreground">{message}</div>
          </div>
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="space-y-3">
          <div className="flex flex-col sm:flex-row gap-3">
            <Input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder={placeholder}
              className="h-11 bg-[var(--color-background)] border-[var(--color-border)] flex-1"
              required
            />
            <Button
              type="submit"
              disabled={status === 'loading'}
              className="h-11 px-6 bg-[oklch(0.62_0.19_150)] hover:bg-[oklch(0.48_0.17_150)] text-[var(--color-background)] font-semibold shadow-lg shadow-[var(--color-glow-soft)] whitespace-nowrap sm:min-w-[140px]"
            >
              {status === 'loading' ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                buttonText
              )}
            </Button>
          </div>
          {status === 'error' && (
            <div className="flex items-center gap-2 text-red-400 text-sm">
              <AlertCircle className="w-4 h-4" />
              {message}
            </div>
          )}
          <p className="text-xs text-muted-foreground">
            We respect your privacy. No spam, unsubscribe anytime.
          </p>
        </form>
      )}
    </div>
  );
}
